import React, { useState, useEffect, useCallback } from 'react';
import { 
  Alert, Box, Breadcrumbs, Button, Card, Container, Dialog, DialogActions, DialogContent, DialogTitle,  
  Grid, Input, LinearProgress, Link, Paper, Snackbar, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, Tabs, Tab, Checkbox, TextField
} from '@material-ui/core';
import Chip from '@mui/material/Chip';
import { CloudUpload, CalendarViewMonth, Send, Delete } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Papa from 'papaparse';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItem from '@mui/material/ListItem';
import ErrorIcon from '@mui/icons-material/Error';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import { fixFrenchCharacters } from './encodingHelper';

export default function Imports() {
  // États principaux
  const [file, setFile] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [progress, setProgress] = useState(0);
  const [alertMessage, setAlertMessage] = useState('');
  const [statusMessages, setStatusMessages] = useState([]);
  const [columnArray, setColumn] = useState([]);
  const [values, setValues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [page, setPage] = useState(0);
  const [openIgnoreConfirm, setOpenIgnoreConfirm] = useState(false);
  const [openOverwriteConfirm, setOpenOverwriteConfirm] = useState(false);
  const [duplicatesToProcess, setDuplicatesToProcess] = useState([]);
  const [processingAll, setProcessingAll] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [results, setResults] = useState({
    crees: 0,
    ecrases: 0,
    ignores: 0,
    duplicates_email: 0,
    duplicates_profile: 0,
    generated_passwords: 0,
    errors: []
  });
  const [importStarted, setImportStarted] = useState(false); // Nouvel état pour gérer l'affichage des boutons

  // États pour la gestion des doublons
  const [pendingDuplicates, setPendingDuplicates] = useState([]);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const columnNames = ['prénom', 'nom', 'profession', 'email', 'mot de passe'];

  // Mémoïzation des fonctions pour éviter les rendus inutiles
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedValues = values.slice(startIndex, endIndex);

  // Téléchargement du modèle CSV
  const downloadExampleCSV = useCallback(() => {
    const csvContent = "\uFEFFprénom;nom;profession;email;mot de passe\r\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modele_import_utilisateurs.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

// Dans votre composant Imports (avant le return)
const formatErrorMessage = (message) => {
    const errorMapping = {
        "'NoneType' object has no attribute 'strip'": "Valeur manquante",
        "La colonne 'email' est manquante ou invalide": "Email manquant",
        "L'email doit contenir un @": "Format d'email invalide",
        // Ajoutez d'autres mappings au besoin
    };
    
    return errorMapping[message] || message;
};

const formatErrorData = (data) => {
    if (!data) return '';
    
    const formattedData = [];
    if (data['prénom']) formattedData.push(`Prénom: ${data['prénom'] || 'non renseigné'}`);
    if (data['nom']) formattedData.push(`Nom: ${data['nom'] || 'non renseigné'}`);
    if (data['profession']) formattedData.push(`Profession: ${data['profession'] || 'non renseigné'}`);
    if (data['email']) formattedData.push(`Email: ${data['email'] || 'non renseigné'}`);
    
    return formattedData.join(' | ');
};



  // Gestion des doublons - Optimisé pour la performance
  const handleSelectDuplicate = useCallback((duplicate) => {
    setSelectedDuplicates(prev => {
      const index = prev.findIndex(d => d.email === duplicate.email);
      if (index >= 0) {
        const newSelected = [...prev];
        newSelected.splice(index, 1);
        return newSelected;
      } else {
        return [...prev, {...duplicate, newEmail: duplicate.email}];
      }
    });
  }, []);

  const handleSelectAllVisibleDuplicates = useCallback(() => {
    const visibleDuplicates = pendingDuplicates.filter(dup => 
      tabValue === 0 || 
      (tabValue === 1 && dup.type === 'email') || 
      (tabValue === 2 && dup.type === 'profile')
    );

    const allSelected = visibleDuplicates.every(dup => 
      selectedDuplicates.some(d => d.email === dup.email)
    );

    if (allSelected) {
      setSelectedDuplicates(prev => 
        prev.filter(d => !visibleDuplicates.some(vd => vd.email === d.email))
      );
    } else {
      const newSelected = [...selectedDuplicates];
      visibleDuplicates.forEach(dup => {
        if (!newSelected.some(d => d.email === dup.email)) {
          newSelected.push({...dup, newEmail: dup.email});
        }
      });
      setSelectedDuplicates(newSelected);
    }
  }, [pendingDuplicates, selectedDuplicates, tabValue]);

  // Traitement des doublons optimisé
  const processConfirmedDuplicates = useCallback(async (duplicates, action) => {
    try {
      const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/confirm-duplicates/`;
      const accessToken = localStorage.getItem("accessToken");

      // Préparation des données optimisée
      const items = duplicates.map(d => ({
        ...d, 
        new_email: d.newEmail, // au lieu de newEmail
        action: action === 'changer_email' ? 'change_email' : action
      }));

      const body = { 
        document_name: documentName,
        items,
        action: action === 'changer_email' ? 'change_email' : action
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Erreur lors de la confirmation');

      const result = await response.json();
      
      // Mise à jour optimisée des résultats
      setResults(prev => {
        const updates = {
          crees: (result.results.created || 0),
          ecrases: (result.results.updated || 0),
          ignores: (result.results.ignored || 0)
        };
        
        return {
          ...prev,
          ...updates,
          duplicates_email: action === 'ignorer' ? 
            prev.duplicates_email - duplicates.filter(i => i.type === 'email').length : 
            prev.duplicates_email,
          duplicates_profile: action === 'ignorer' ? 
            prev.duplicates_profile - duplicates.filter(i => i.type === 'profile').length : 
            prev.duplicates_profile
        };
      });

      setStatusMessages(prev => [
        ...prev,
        `${duplicates.length} doublon(s) ${getActionMessage(action)}`
      ]);

      // Filtrage optimisé des doublons traités
      setPendingDuplicates(prev => {
        const processedEmails = new Set(duplicates.map(d => d.email));
        return prev.filter(dup => !processedEmails.has(dup.email));
      });

      return true;
    } catch (error) {
      setStatusMessages(prev => [
        ...prev, 
        `Erreur lors du traitement: ${error.message}`
      ]);
      return false;
    }
  }, [documentName]);

  const getActionMessage = (action) => {
    switch(action) {
      case 'creer': return 'créés';
      case 'ecraser':
      case 'changer_email': return 'mis à jour';
      default: return 'ignorés';
    }
  };

  // Actions sur les doublons optimisées
  const handleIgnoreSelected = useCallback(async () => {
    const duplicatesToProcess = selectedDuplicates.length > 0 
      ? selectedDuplicates 
      : pendingDuplicates.filter(dup => 
          tabValue === 0 || 
          (tabValue === 1 && dup.type === 'email') || 
          (tabValue === 2 && dup.type === 'profile')
        );
  
    if (duplicatesToProcess.length === 0) {
      setAlertMessage('Aucun doublon à ignorer');
      return;
    }
  
    setDuplicatesToProcess(duplicatesToProcess);
    setProcessingAll(selectedDuplicates.length === 0);
    setOpenIgnoreConfirm(true);
  }, [selectedDuplicates, pendingDuplicates, tabValue]);

  const handleOverwriteSelected = useCallback(async () => {
    if (selectedDuplicates.length === 0) {
      setAlertMessage('Aucun doublon sélectionné');
      return;
    }

    if (selectedDuplicates.some(d => d.type === 'email')) {
      setAlertMessage('Impossible d\'écraser un doublon email - choisissez "Changer email" ou "Ignorer"');
      return;
    }

    setDuplicatesToProcess(selectedDuplicates);
    setProcessingAll(false);
    setOpenOverwriteConfirm(true);
  }, [selectedDuplicates]);

  const handleCreateSelected = useCallback(async () => {
    if (selectedDuplicates.length === 0) {
      setAlertMessage('Aucun doublon sélectionné');
      return;
    }

    if (selectedDuplicates.some(d => d.type === 'email')) {
      setAlertMessage('Impossible de créer un doublon email - choisissez "Changer email" ou "Ignorer"');
      return;
    }

    const success = await processConfirmedDuplicates(selectedDuplicates, 'creer');
    if (success) {
      setSelectedDuplicates([]);
      if (pendingDuplicates.length === selectedDuplicates.length) {
        setOpenConfirmDialog(false);
      }
    }
  }, [selectedDuplicates, processConfirmedDuplicates, pendingDuplicates]);

  const handleChangeEmailSelected = useCallback(async () => {
    if (selectedDuplicates.length === 0) {
      setAlertMessage('Aucun doublon sélectionné');
      return;
    }

    if (selectedDuplicates.some(d => d.type !== 'email')) {
      setAlertMessage('Vous ne pouvez changer que les emails des doublons de type email');
      return;
    }

    if (selectedDuplicates.some(d => !d.newEmail || !d.newEmail.includes('@'))) {
      setAlertMessage('Veuillez entrer un nouvel email valide pour tous les doublons sélectionnés');
      return;
    }

    const success = await processConfirmedDuplicates(selectedDuplicates, 'changer_email');
    if (success) {
      setSelectedDuplicates([]);
      if (pendingDuplicates.length === selectedDuplicates.length) {
        setOpenConfirmDialog(false);
      }
    }
  }, [selectedDuplicates, processConfirmedDuplicates, pendingDuplicates]);

  const handleConfirmIgnore = useCallback(async () => {
    setOpenIgnoreConfirm(false);
    const success = await processConfirmedDuplicates(duplicatesToProcess, 'ignorer');
    if (success) {
      setSelectedDuplicates([]);
      if (processingAll || pendingDuplicates.length === duplicatesToProcess.length) {
        setOpenConfirmDialog(false);
      }
    }
  }, [duplicatesToProcess, processingAll, pendingDuplicates, processConfirmedDuplicates]);

  const handleConfirmOverwrite = useCallback(async () => {
    setOpenOverwriteConfirm(false);
    const success = await processConfirmedDuplicates(duplicatesToProcess, 'overwrite'); // Changé de 'ecraser' à 'overwrite'
    if (success) {
        setSelectedDuplicates([]);
        if (processingAll || pendingDuplicates.length === duplicatesToProcess.length) {
            setOpenConfirmDialog(false);
        }
    }
}, [duplicatesToProcess, processingAll, pendingDuplicates, processConfirmedDuplicates]);

  // Import des utilisateurs optimisé avec barre de progression
  const importUsers = useCallback(async () => {
    setImportStarted(true); // Marquer que l'importation a commencé
    setIsLoading(true);
    setProgress(0);
    setAlertMessage('');
    setStatusMessages([]);
    setPendingDuplicates([]);
    setSelectedDuplicates([]);
    
    if (!file) {
      setAlertMessage('Aucun fichier sélectionné');
      setIsLoading(false);
      return;
    }

    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/import-users/`;
    const accessToken = localStorage.getItem("accessToken");

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_name', documentName || selectedFileName);

    try {
      const controller = new AbortController();
      const signal = controller.signal;

      // Simulation de progression pour l'UI
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + Math.random() * 10;
          return newProgress > 90 ? 90 : newProgress;
        });
      }, 500);

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        },
        body: formData,
        signal
      });

      clearInterval(progressInterval);
      setProgress(95); // Presque terminé

      if (!resp.ok) {
        // Essayez de parser le corps de la réponse comme JSON
        let errorMessage = `HTTP error! Status: ${resp.status}`;
        try {
          const errorData = await resp.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          console.error("Failed to parse error response", e);
        }
        throw new Error(errorMessage);
      }
      const json = await resp.json();
      setProgress(100); // Terminé
      
      if (json.status === 'success') {
        setResults({
          ...json.results,
          crees: json.results.created || 0,
          ecrases: 0,
          ignores: 0
        });
        
        if (json.results.pending_duplicates?.length > 0) {
          setPendingDuplicates(json.results.pending_duplicates);
          setOpenConfirmDialog(true);
          setAlertMessage('Confirmation des doublons nécessaire');
        } else {
          setAlertMessage('Importation terminée avec succès');
        }
        
        const messages = [];
        if (json.results.created > 0) {
          messages.push(`${json.results.created} utilisateur(s) créé(s)`);
        }
        if (json.results.duplicates_email > 0) {
          messages.push(`${json.results.duplicates_email} doublon(s) email détecté(s)`);
        }
        if (json.results.duplicates_profile > 0) {
          messages.push(`${json.results.duplicates_profile} doublon(s) profil détecté(s)`);
        }
        if (json.results.generated_passwords > 0) {
          messages.push(`${json.results.generated_passwords} mot(s) de passe généré(s)`);
        }
        if (json.results.errors.length > 0) {
          messages.push(`${json.results.errors.length} erreur(s) lors de l'importation`);
          json.results.errors.forEach(error => {
            messages.push(`Ligne ${error.line}: ${error.message}`);
          });
        }
        
        setStatusMessages(messages);
      } else {
        setAlertMessage('Erreur lors de l\'importation');
        setStatusMessages([json.error || 'Erreur inconnue']);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error importing users:', error);
        setAlertMessage('Erreur lors de l\'importation');
        setStatusMessages([error.message]); // Affiche maintenant le message détaillé
      }
    } finally {
      setIsLoading(false);
    }
  }, [file, documentName, selectedFileName]);
  

  // Gestion du fichier uploadé optimisée
  const handleFileChange = useCallback((event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    setImportStarted(false); // Réinitialiser l'état d'importation quand un nouveau fichier est sélectionné
    setIsLoading(true);
    setProgress(10); // Début du traitement
    setFile(selectedFile);
    setResults({
      crees: 0,
      ecrases: 0,
      ignores: 0,
      duplicates_email: 0,
      duplicates_profile: 0,
      generated_passwords: 0,
      errors: []
    });
    setStatusMessages([]);
    setPendingDuplicates([]);
    setSelectedDuplicates([]);
    
    const fileNameWithoutExtension = selectedFile.name.split('.').slice(0, -1).join('.');
    setDocumentName(fileNameWithoutExtension);
    setSelectedFileName(fileNameWithoutExtension);
  
    // Utilisation de FileReader pour mieux contrôler l'encodage
    const reader = new FileReader();
    reader.onload = (e) => {
      setProgress(30); // Fichier chargé
      
      // Convertir en Uint8Array pour détection précise de l'encodage
      const content = new Uint8Array(e.target.result);
      let decodedContent;
      
      // Essayer UTF-8 d'abord
      try {
        decodedContent = new TextDecoder('utf-8').decode(content);
        if (decodedContent.includes('�')) { // Si caractères mal décodés
          // Essayer Windows-1252 (common pour les fichiers français)
          decodedContent = new TextDecoder('windows-1252').decode(content);
        }
      } catch {
        // Fallback simple si les autres méthodes échouent
        decodedContent = e.target.result;
      }

      setProgress(50); // Décodage terminé
      
      Papa.parse(decodedContent, {
        encoding: 'UTF-8',
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          setProgress(70); // Parsing CSV terminé
          
          // Traitement des données optimisé
          const processedData = result.data.map(row => {
            return Object.keys(row).reduce((acc, key) => {
              // Nettoyage des caractères
              acc[key] = row[key] ? fixFrenchCharacters(row[key]) : row[key];
              return acc;
            }, {});
          });

          if (processedData.length > 0) {
            setColumn(Object.keys(processedData[0]));
            setValues(processedData.map(row => Object.values(row)));
          }
          setProgress(100); // Traitement complet
          setIsLoading(false);
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          setStatusMessages(['Erreur lors de la lecture du fichier CSV']);
          setProgress(100);
          setIsLoading(false);
        }
      });
    };
    reader.onerror = () => {
      setStatusMessages(['Erreur lors de la lecture du fichier']);
      setProgress(100);
      setIsLoading(false);
    };
    reader.readAsArrayBuffer(selectedFile); // Lire comme ArrayBuffer pour meilleur contrôle
  }, []);

  // Annulation de l'upload
  const handleCancelUpload = useCallback(() => {
    setFile(null);
    setProgress(0);
    setAlertMessage('');
    setStatusMessages([]);
    setColumn([]);
    setValues([]);
    setSelectedFileName('');
    setDocumentName('');
    setPendingDuplicates([]);
    setSelectedDuplicates([]);
    setImportStarted(false); // Réinitialiser l'état d'importation

  }, []);

  // Effet pour le titre de la page
  useEffect(() => {
    document.title = "Imports Utilisateurs | Syndays";
  }, []);

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 8
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <Typography color="textPrimary" variant="h5">
                Importation d'utilisateurs
              </Typography>
              <Breadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRightIcon fontSize="small" />}
                sx={{ mt: 1 }}
              >
               
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <input
  type="file"
  accept=".csv"
  onChange={handleFileChange}
  style={{ display: 'none' }}
  id="import_b"
/>
                  <label htmlFor="import_b">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUpload />}
                      sx={{ width: '200px', height: '50px' }}
                    >
                      Choisir un fichier
                    </Button>
                  </label>
                  
                  {file && (
                    <TextField
                      label="Nom du document"
                      value={documentName}
                      onChange={(e) => setDocumentName(e.target.value)}
                      variant="outlined"
                      sx={{ width: '300px' }}
                    />
                  )}
                  
                  <Button
                    variant="contained"
                    onClick={downloadExampleCSV}
                    startIcon={<CalendarViewMonth />}
                    sx={{ width: '300px', height: '50px' }}
                  >
                    Télécharger le modèle d'importation
                  </Button>
                  
                  {file && (
                    <>
                    {!importStarted ? (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={importUsers}
                        startIcon={<Send />}
                        sx={{ width: '200px', height: '50px' }}
                      >
                        Importer
                      </Button>
                      ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCancelUpload}
                        startIcon={<Delete />}
                        sx={{ width: '200px', height: '50px' }}
                      >
                        Annuler
                      </Button>
                             )}
                    </>
                  )}
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography paragraph>
                    Le fichier doit être de type CSV avec les colonnes suivantes (séparées par des point-virgules) :
                  </Typography>
                  <Typography paragraph>
                    prénom; nom; profession; email; mot de passe
                  </Typography>
                  <Typography>
                    <strong>Note :</strong> Le mot de passe est optionnel.
                  </Typography>
                </Alert>
                
                {isLoading && (
                  <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                    <Typography variant="body2" align="right" sx={{ mt: 1 }}>
                      {Math.round(progress)}% complet
                    </Typography>
                  </Box>
                )}
                
                {alertMessage && (
                  <Typography
                    variant="body1"
                    sx={{ 
                      color: alertMessage.includes('succès') ? 'green' : 'error.main',
                      fontWeight: 'bold',
                      mt: 1
                    }}
                  >
                    {alertMessage}
                  </Typography>
                )}
              </Paper>
              
              {statusMessages.length > 0 && (
  <Card elevation={3} sx={{ padding: 2, mb: 2 }}>
    <Typography variant="h6" gutterBottom>
      Résultats de l'importation :
    </Typography>
                  
                  <Box sx={{ mb: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Statistiques :
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 , marginTop:2 }}>
                      <Chip 
                        label={`${results.crees} créé(s)`} 
                        color="success" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${results.ecrases} écrasé(s)`} 
                        color="warning" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${results.ignores} ignoré(s)`} 
                        color="info" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${results.duplicates_email} doublon(s) email`} 
                        color="error" 
                        variant="outlined" 
                      />
                      <Chip 
                        label={`${results.duplicates_profile} doublon(s) profil`} 
                        color="error" 
                        variant="outlined" 
                      />
                    </Box>
                  </Box>

                  {results.errors.length > 0 && (
    <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
            Problèmes détectés dans votre fichier :
        </Typography>
        <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {results.errors.map((error, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                        <ErrorIcon color="error" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                         primary={`Ligne ${error.line}: ${formatErrorMessage(error.message)}`}
                         secondary={formatErrorData(error.data)}
                         primaryTypographyProps={{ variant: 'body2', color: 'error.main' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                    />
                </ListItem>
            ))}
        </List>
    </Box>
)}

                  {statusMessages.filter(msg => !msg.startsWith('Ligne')).map((message, index) => (
                    <Typography 
                      key={index} 
                      variant="body1" 
                      sx={{ 
                        mb: 1,
                        color: message.includes('erreur') ? 'error.main' : 'inherit'
                      }}
                    >
                      {message}
                    </Typography>
                  ))}
                </Card>
              )}
              
              {columnArray.length > 0 && (
                <Paper elevation={3} sx={{ padding: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    Aperçu des données à importer :
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {columnNames.map((col, i) => (
                            <TableCell key={i} sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                              {col}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedValues.map((v, i) => (
                          <TableRow key={i}>
                            {v.map((value, j) => (
                              <TableCell key={j}>
                                {j === 4 ? '••••••••' : (
                                  <span>{fixFrenchCharacters(value)}</span>
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={values.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Paper>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Dialog de confirmation des doublons optimisé */}
      <Dialog 
        open={openConfirmDialog} 
        onClose={() => {
          setOpenConfirmDialog(false);
          setSelectedDuplicates([]);
        }}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle>Gestion des doublons</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            {pendingDuplicates.length} doublon(s) détecté(s). Sélectionnez les doublons à traiter :
          </Typography>
          
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => {
                setTabValue(newValue);
                setSelectedDuplicates([]);
              }}
              aria-label="onglets doublons"
            >
              <Tab label="Tous les doublons" />
              <Tab label="Doublons email" />
              <Tab label="Doublons profil" />
            </Tabs>
          </Box>

          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '50vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selectedDuplicates.length > 0 &&
                        selectedDuplicates.length < pendingDuplicates.filter(dup => 
                          tabValue === 0 || 
                          (tabValue === 1 && dup.type === 'email') || 
                          (tabValue === 2 && dup.type === 'profile')
                        ).length
                      }
                      checked={
                        pendingDuplicates.filter(dup => 
                          tabValue === 0 || 
                          (tabValue === 1 && dup.type === 'email') || 
                          (tabValue === 2 && dup.type === 'profile')
                        ).length > 0 &&
                        selectedDuplicates.length === pendingDuplicates.filter(dup => 
                          tabValue === 0 || 
                          (tabValue === 1 && dup.type === 'email') || 
                          (tabValue === 2 && dup.type === 'profile')
                        ).length
                      }
                      onChange={handleSelectAllVisibleDuplicates}
                    />
                  </TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Ligne</TableCell>
                  <TableCell>Prénom</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Profession</TableCell>
                  <TableCell>Email</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingDuplicates
                  .filter(dup => 
                    tabValue === 0 || 
                    (tabValue === 1 && dup.type === 'email') || 
                    (tabValue === 2 && dup.type === 'profile')
                  )
                  .map((dup, index) => (
                    <TableRow key={index} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDuplicates.some(d => d.email === dup.email)}
                          onChange={() => handleSelectDuplicate(dup)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={dup.type === 'email' ? 'Email' : 'Profil'} 
                          color={dup.type === 'email' ? 'primary' : 'secondary'} 
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{dup.line}</TableCell>
                      <TableCell>{dup.firstname}</TableCell>
                      <TableCell>{dup.lastname}</TableCell>
                      <TableCell>{dup.profession}</TableCell>
                      <TableCell>
                        {selectedDuplicates.some(d => d.email === dup.email && d.type === 'email') ? (
                          <TextField
                            value={selectedDuplicates.find(d => d.email === dup.email)?.newEmail || dup.email}
                            onChange={(e) => {
                              const updated = selectedDuplicates.map(d => 
                                d.email === dup.email ? {...d, newEmail: e.target.value} : d
                              );
                              setSelectedDuplicates(updated);
                            }}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          dup.email
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Typography variant="body2" color="textSecondary">
            {selectedDuplicates.length > 0 ? `${selectedDuplicates.length} sélectionné(s)` : 'Aucune sélection'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained"
              onClick={handleIgnoreSelected}
              color="primary"
            >
              {selectedDuplicates.length > 0 ? `Ignorer (${selectedDuplicates.length})` : 'Ignorer tout'}
            </Button>
            <Button 
              variant="contained"
              onClick={handleChangeEmailSelected}
              color="secondary"
              disabled={selectedDuplicates.length === 0 || selectedDuplicates.some(d => d.type !== 'email')}
            >
              Changer email
            </Button>
            <Button 
              variant="contained"
              onClick={handleCreateSelected}
              color="success"
              disabled={selectedDuplicates.length === 0 || selectedDuplicates.some(d => d.type === 'email')}
            >
              Créer
            </Button>
            <Button 
              variant="contained"
              onClick={handleOverwriteSelected}
              color="warning"
              disabled={selectedDuplicates.length === 0 || selectedDuplicates.some(d => d.type === 'email')}
            >
              Écraser
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      
      {/* Boîte de dialogue de confirmation pour Ignorer */}
      <Dialog
        open={openIgnoreConfirm}
        onClose={() => setOpenIgnoreConfirm(false)}
      >
        <DialogTitle>Confirmer l'ignorance</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir ignorer {duplicatesToProcess.length} doublon(s) ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIgnoreConfirm(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmIgnore} color="primary" variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Boîte de dialogue de confirmation pour Écraser */}
      <Dialog
        open={openOverwriteConfirm}
        onClose={() => setOpenOverwriteConfirm(false)}
      >
        <DialogTitle>Confirmer l'écrasement</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Attention: Cette action supprimera définitivement les comptes existants.
          </Alert>
          <Typography>
            Êtes-vous sûr de vouloir écraser {duplicatesToProcess.length} doublon(s) ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOverwriteConfirm(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmOverwrite} color="warning" variant="contained">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}